import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";

const IRISH_COUNTIES = [
  "Antrim","Armagh","Carlow","Cavan","Clare","Cork","Derry","Donegal","Down","Dublin",
  "Fermanagh","Galway","Kerry","Kildare","Kilkenny","Laois","Leitrim","Limerick","Longford",
  "Louth","Mayo","Meath","Monaghan","Offaly","Roscommon","Sligo","Tipperary","Tyrone",
  "Waterford","Westmeath","Wexford","Wicklow",
];

const TITLES = {
  domestic: "Enquire about this mower",
  commercial: "Commercial mowing enquiry",
  line_marking: "Line marking enquiry",
  assessment: "Book a site assessment",
  service: "Service & repair enquiry",
  general: "Send us an enquiry",
};

/**
 * Reusable enquiry dialog.
 * props: open, onOpenChange, type, product (optional {id,name}), sourcePage
 */
export function EnquiryDialog({ open, onOpenChange, type = "general", product, sourcePage }) {
  const commercial = type === "commercial" || type === "line_marking" || type === "assessment";
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", county: "", message: "",
    organisation: "", area_estimate: "", pitches: "", service_type: "", timeframe: "",
    company: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setForm({
      name: "", email: "", phone: "", county: "", message: "",
      organisation: "", area_estimate: "", pitches: "", service_type: "", timeframe: "",
      company: "",
    });
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        type,
        product_id: product?.id || null,
        product_name: product?.name || null,
        source_page: sourcePage || (typeof window !== "undefined" ? window.location.pathname : null),
      };
      const res = await api.post("/enquiries", payload);
      setDone(true);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enquiry-dialog">
        {done ? (
          <div className="py-8 text-center" data-testid="enquiry-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-brand-navy" />
            <h3 className="mt-4 text-2xl font-display font-bold text-brand-ink">Enquiry received</h3>
            <p className="mt-2 text-muted-foreground">
              Your enquiry has been received. Our team will contact you to discuss your requirements.
            </p>
            <Button className="mt-6 bg-brand-navy hover:bg-brand-navy/90 text-white" onClick={() => handleOpenChange(false)} data-testid="enquiry-close-button">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-brand-navy">{TITLES[type]}</DialogTitle>
              <DialogDescription>
                {product?.name
                  ? `About: ${product.name}`
                  : "Tell us a little about your requirements and we'll be in touch."}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 grid gap-4">
              <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="enq-name">Name *</Label>
                  <Input id="enq-name" data-testid="enquiry-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} required />
                </div>
                <div>
                  <Label htmlFor="enq-email">Email *</Label>
                  <Input id="enq-email" type="email" data-testid="enquiry-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} required />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="enq-phone">Phone</Label>
                  <Input id="enq-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
                <div>
                  <Label>County</Label>
                  <Select value={form.county} onValueChange={(v) => set("county", v)}>
                    <SelectTrigger data-testid="enquiry-county-select"><SelectValue placeholder="Select county" /></SelectTrigger>
                    <SelectContent>
                      {IRISH_COUNTIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {commercial && (
                <>
                  <div>
                    <Label htmlFor="enq-org">Organisation / Club</Label>
                    <Input id="enq-org" data-testid="enquiry-org-input" value={form.organisation} onChange={(e) => set("organisation", e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="enq-area">Approx. area (m²) or "unknown"</Label>
                      <Input id="enq-area" data-testid="enquiry-area-input" value={form.area_estimate} onChange={(e) => set("area_estimate", e.target.value)} placeholder="Unknown is fine" />
                    </div>
                    <div>
                      <Label htmlFor="enq-pitches">Number of pitches</Label>
                      <Input id="enq-pitches" data-testid="enquiry-pitches-input" value={form.pitches} onChange={(e) => set("pitches", e.target.value)} placeholder="Optional" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Mowing / Marking / Both</Label>
                      <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                        <SelectTrigger data-testid="enquiry-servicetype-select"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mowing">Mowing</SelectItem>
                          <SelectItem value="marking">Line marking</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Timeframe</Label>
                      <Select value={form.timeframe} onValueChange={(v) => set("timeframe", v)}>
                        <SelectTrigger data-testid="enquiry-timeframe-select"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asap">As soon as possible</SelectItem>
                          <SelectItem value="1-3 months">1–3 months</SelectItem>
                          <SelectItem value="3-6 months">3–6 months</SelectItem>
                          <SelectItem value="exploring">Just exploring</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="enq-message">Message</Label>
                <Textarea id="enq-message" data-testid="enquiry-message-input" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="How can we help?" />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="submit" disabled={submitting} className="w-full bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold" data-testid="enquiry-submit-button">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Send enquiry"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
